import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Loader2, Plane } from 'lucide-react';
import TourCard from '../components/tour/TourCard';
import WhatsAppGroupButton from '../components/ui/WhatsAppGroupButton';
import CustomTourForm from '../components/forms/CustomTourForm';
import { useTours } from '../hooks/useTours';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { appConfig } from '../config/appConfig';

const ToursPage: React.FC = () => {
  const { ref, isVisible } = useScrollReveal();
  const { tours, loading, error } = useTours();

  // Filter tours by status
  const availableTours = tours.filter(tour => tour.status === 'active');
  const soldOutTours = tours.filter(tour => tour.status === 'sold_out');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  return (
    <div className="min-h-screen gradient-cream-br">
      {/* Page Header */}
      <motion.div 
        className="gradient-primary text-white py-16"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 rounded-full mb-6">
              <Plane size={20} />
              <span className="body-small font-semibold">
                Explore the World
              </span>
            </div>
            
            <h1 className="heading-primary mb-6">
              Discover Amazing Tours
            </h1>
            
            <p className="body-large max-w-3xl mx-auto opacity-90 mb-8">
              Embark on unforgettable journeys to breathtaking destinations. 
              Our carefully curated tours offer the perfect blend of adventure, 
              culture, and luxury for the modern traveler.
            </p>

            {/* Stats */}
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{availableTours.length}</div>
                <div className="body-small opacity-80">Available Tours</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{appConfig.metrics.destinationCount}+</div>
                <div className="body-small opacity-80">Destinations</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{appConfig.metrics.happyTravelers.toLocaleString()}+</div>
                <div className="body-small opacity-80">Happy Travelers</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Tours Section */}
      <section ref={ref} className="py-16">
        <div className="container">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
            className="mb-12"
          >
            {/* Section Header */}
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h2 className="heading-secondary text-neutral-charcoal mb-4">
                Available Tours
              </h2>
              <p className="body-large text-secondary-slate max-w-2xl mx-auto mb-6">
                Stay connected with fellow travelers and get tour updates in our community group.
              </p>
              <WhatsAppGroupButton />
            </motion.div>

            {/* Tours Grid */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8"
              variants={containerVariants}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
            >
              {loading ? (
                // Loading state
                <div className="col-span-full flex items-center justify-center py-12">
                  <div className="flex items-center gap-3 text-secondary-slate">
                    <Loader2 size={24} className="animate-spin" />
                    <span className="body-large">Loading tours...</span>
                  </div>
                </div>
              ) : error || availableTours.length === 0 ? (
                <div className="col-span-full flex items-center justify-center py-12">
                  <div className="text-center">
                    <MapPin size={48} className="text-secondary-slate mx-auto mb-4" />
                    <h3 className="heading-tertiary text-secondary-slate mb-2">No Tours Available</h3>
                    <p className="body-large text-secondary-slate">
                      Check back later for upcoming tours and adventures.
                    </p>
                  </div>
                </div>
              ) : (
                // Available tours display
                availableTours.map((tour, index) => (
                  <motion.div
                    key={tour.tourId}
                    initial={{ opacity: 0, y: 50 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : {}}
                    transition={{ 
                      duration: 0.6, 
                      delay: index * 0.1,
                      ease: 'easeOut' 
                    }}
                  >
                    <TourCard tour={tour} index={index} />
                  </motion.div>
                ))
              )}
            </motion.div>
          </motion.div>

          {/* Sold Out Tours Section */}
          {!loading && !error && soldOutTours.length > 0 && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              className="mb-12"
            >
              {/* Section Header */}
              <motion.div
                className="text-center mb-12"
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <h2 className="heading-secondary text-neutral-charcoal mb-4">
                  Sold Out Tours
                </h2>
                <p className="body-large text-secondary-slate max-w-2xl mx-auto">
                  These tours have reached full capacity. Check back for future dates or contact us for similar experiences.
                </p>
              </motion.div>

              {/* Sold Out Tours Grid */}
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8"
                variants={containerVariants}
                initial="hidden"
                animate={isVisible ? "visible" : "hidden"}
              >
                {soldOutTours.map((tour, index) => (
                  <motion.div
                    key={tour.tourId}
                    initial={{ opacity: 0, y: 50 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : {}}
                    transition={{ 
                      duration: 0.6, 
                      delay: index * 0.1,
                      ease: 'easeOut' 
                    }}
                  >
                    <TourCard tour={tour} index={index} />
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          )}

          {/* Custom Tour Selection */}
          <motion.div
            className="mt-16 gradient-primary rounded-3xl p-8 md:p-12"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 rounded-full mb-6">
                <Plane size={20} className="text-white" />
                <span className="body-small font-semibold text-white">
                  Custom Travel Planning
                </span>
              </div>
              <h3 className="heading-secondary text-white mb-6">
                Plan Your Perfect Journey
              </h3>
              <p className="body-large text-white opacity-90 max-w-3xl mx-auto">
                Let us create a customized travel experience just for you. 
                Our travel experts are ready to craft your dream journey.
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto">
              <CustomTourForm />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ToursPage;
